import { recruitingAPI } from "../../services/recruitingApi";
import {
  createPersonnelAssignmentHook,
  type UsePersonnelAssignmentOptions,
  type AssignedPerson,
  type AvailablePerson,
  type NotificationState,
  type ConfirmDialogState,
} from "./usePersonnelAssignment";

export interface AssignedRecruiter extends AssignedPerson {
  recruiter_id: number;
  recruiter_name?: string;
}

export type AvailableRecruiter = AvailablePerson;

const usePersonnelAssignment = createPersonnelAssignmentHook<AssignedRecruiter>(
  {
    roleSlug: "recruiter",
    assignedQueryKey: "candidate-recruiters",
    availableQueryKey: "recruiters-list",
    assignedDataKey: "assigned_recruiters",
    fetchAssigned: (candidateId) =>
      recruitingAPI.getRecruitersForCandidate(candidateId),
    assign: (candidateId, recruiterId) =>
      recruitingAPI.assignRecruiterToCandidate(candidateId, recruiterId),
    remove: (candidateId, recruiterId) =>
      recruitingAPI.removeRecruiterFromCandidate(candidateId, recruiterId),
    label: "Recruiter",
    gateAssignedOnPermission: false,
    getPersonnelId: (r) => r.recruiter_id,
  },
);

export interface UseRecruiterAssignmentReturn {
  recruiters: AssignedRecruiter[];
  availableRecruiters: AvailableRecruiter[];
  selectedRecruiterId: string;
  setSelectedRecruiterId: (id: string) => void;
  isAssigningRecruiter: boolean;
  handleAssignRecruiter: (overrideId?: string) => Promise<void>;
  handleRemoveRecruiter: (recruiterId: number) => void;
  canManageRecruiters: boolean;
  notification: NotificationState;
  closeNotification: () => void;
  confirmDialog: ConfirmDialogState;
  confirmRemoval: () => void;
  cancelRemoval: () => void;
}

/**
 * Custom hook for managing recruiter assignments for a candidate.
 * Wraps the shared usePersonnelAssignment hook with recruiter-specific naming.
 */
export function useRecruiterAssignment(
  options: UsePersonnelAssignmentOptions,
): UseRecruiterAssignmentReturn {
  const result = usePersonnelAssignment(options);

  return {
    recruiters: result.assigned,
    availableRecruiters: result.available,
    selectedRecruiterId: result.selectedId,
    setSelectedRecruiterId: result.setSelectedId,
    isAssigningRecruiter: result.isAssigning,
    handleAssignRecruiter: result.handleAssign,
    handleRemoveRecruiter: result.handleRemove,
    canManageRecruiters: result.canManage,
    notification: result.notification,
    closeNotification: result.closeNotification,
    confirmDialog: result.confirmDialog,
    confirmRemoval: result.confirmRemoval,
    cancelRemoval: result.cancelRemoval,
  };
}

export default useRecruiterAssignment;
